// src/common/context/auth.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

type AuthContextValue = {
  appkey: string | null;
  authkey: string | null;
  setKeys: (appkey: string, authkey: string) => void;
  clearKeys: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_APPKEY = "botlinkd_appkey";
const STORAGE_AUTHKEY = "botlinkd_authkey";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [appkey, setAppkey] = useState<string | null>(null);
  const [authkey, setAuthkey] = useState<string | null>(null);

  // Load from localStorage once on mount
  useEffect(() => {
    try {
      const storedApp = window.localStorage.getItem(STORAGE_APPKEY);
      const storedAuth = window.localStorage.getItem(STORAGE_AUTHKEY);

      if (storedApp && storedAuth) {
        setAppkey(storedApp);
        setAuthkey(storedAuth);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const setKeys = (app: string, auth: string) => {
    setAppkey(app);
    setAuthkey(auth);

    try {
      window.localStorage.setItem(STORAGE_APPKEY, app);
      window.localStorage.setItem(STORAGE_AUTHKEY, auth);
    } catch {
      // ignore storage errors
    }
  };

  const clearKeys = () => {
    setAppkey(null);
    setAuthkey(null);

    try {
      window.localStorage.removeItem(STORAGE_APPKEY);
      window.localStorage.removeItem(STORAGE_AUTHKEY);
    } catch {
      // ignore storage errors
    }
  };

  const value: AuthContextValue = {
    appkey,
    authkey,
    setKeys,
    clearKeys,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
